/**
 * Global Search (Cmd / Ctrl + K).
 *
 * Listens for the `tc:open-search` window event and renders a Command
 * Palette using the `cmdk` Command primitive. Searches three entities in
 * parallel via existing list endpoints and groups the results.
 *
 * NOTE: The result-rendering sub-blocks are extracted into `SearchResults`
 * to avoid the deep nested-ternary JSX pattern that the visual-edits Babel
 * plugin in this repo crashes on with "Maximum call stack size exceeded".
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
} from '@/components/ui/command';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import SearchResults from './GlobalSearchResults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ travellers: [], trips: [], invoices: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = () => { setOpen(true); setQ(''); };
    window.addEventListener('tc:open-search', handler);
    return () => window.removeEventListener('tc:open-search', handler);
  }, []);

  // 200ms debounce — feels instant without flooding the API.
  useEffect(() => {
    if (!open) return;
    if (!q || q.length < 2) {
      setResults({ travellers: [], trips: [], invoices: [] });
      return;
    }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const [tr, tp, iv] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/clients?search=${encodeURIComponent(q)}&limit=5`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/trips?search=${encodeURIComponent(q)}&limit=5`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/invoices?search=${encodeURIComponent(q)}&limit=5`).catch(() => ({ data: [] })),
        ]);
        const norm = (r) => Array.isArray(r.data) ? r.data : (r.data && r.data.items) || [];
        setResults({
          travellers: norm(tr).slice(0, 5),
          trips: norm(tp).slice(0, 5),
          invoices: norm(iv).slice(0, 5),
        });
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(id);
  }, [q, open]);

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      {/* Hidden but present for screen-reader / Radix a11y compliance. */}
      <DialogTitle className="sr-only">Global search</DialogTitle>
      <DialogDescription className="sr-only">Search travellers, trips, and invoices.</DialogDescription>
      <CommandInput
        placeholder="Search travellers, trips, invoices…"
        value={q}
        onValueChange={setQ}
        data-testid="global-search-input"
      />
      <CommandList data-testid="global-search-results">
        <SearchResults q={q} loading={loading} results={results} go={go} />
        <CommandEmpty>No matches found.</CommandEmpty>
      </CommandList>
    </CommandDialog>
  );
};

export default GlobalSearch;
